/**
 * Guided tour — step definitions.
 *
 * Each step has:
 *   id:         stable key (also used as React key)
 *   title:      short header
 *   body:       one or two sentences — keep short, people read on autopilot
 *   target:     CSS selector OR null to center the tooltip
 *   placement:  'top' | 'bottom' | 'left' | 'right' | 'center' (default 'bottom')
 *   media:      optional, one of:
 *                 { kind: 'image', src, alt }
 *                 { kind: 'video', src, poster? }
 *                 { kind: 'iframe', src, title }  // for Loom / YouTube embeds
 *
 * Media paths: drop GIFs / MP4s into `public/tour/<name>` and reference
 * them with `src: '/tour/<name>.gif'`. They'll ship with the app.
 *
 * Keep the total under ~16 steps. People tap through fast.
 */
export const TOUR_STEPS = [
  {
    id: 'welcome',
    title: '👋 Welcome to the Q2 Goals Dashboard',
    body:
      'Quick 2-minute tour of the main features. Click Next to begin, or Skip if you want to explore on your own. You can replay this any time from the 🧭 Tour button in the top nav.',
    target: null,
    placement: 'center',
  },
  {
    id: 'pillars',
    title: '🏛️ Six pillars',
    body:
      'Each tab is a pillar of Q2 goals — EO Buyouts, Revenue, Growth, Delivery & Product, Team & Culture, AI Systems. Click any tab to switch; your work stays synced across pillars.',
    target: '[data-tour="pillar-tabs"]',
    placement: 'bottom',
    media: { kind: 'demo', id: 'pillars' },
  },
  {
    id: 'exec-summary',
    title: '✨ Executive Summary',
    body:
      'This is what Zoe reviews each week to write her company rollup — the Monday recap that goes out to the whole company. It\'s AI-generated from everyone\'s weekly TLDRs, Wins, and Flags, so the quality of your inputs directly shapes what she sends.',
    target: '[data-tour="exec-summary"]',
    placement: 'bottom',
    media: { kind: 'demo', id: 'exec-summary' },
  },
  {
    id: 'follow-ups',
    title: '🎯 Follow-ups',
    body:
      'Every open action item across all pillars in one place. Check items off as you finish — overdue ones are flagged red. This is the "what needs attention now" view for the whole team.',
    target: '[data-tour="follow-ups"]',
    placement: 'bottom',
    media: { kind: 'demo', id: 'follow-ups' },
  },
  {
    id: 'archive',
    title: '📅 Weekly Archive',
    body:
      'Browse prior weekly snapshots — TLDRs, Wins, Flags, and action items frozen at that moment. Snapshots run automatically Tuesday at 5pm PST so by Wednesday morning you have a clean record of the week that just ended.',
    target: '[data-tour="archive-button"]',
    placement: 'bottom',
    media: { kind: 'demo', id: 'archive' },
  },
  {
    id: 'presence',
    title: '👥 Who\'s here',
    body:
      'Live presence shows who\'s currently on the dashboard and which pillar they\'re on. Click any teammate\'s pill to start a 1:1 DM with them.',
    target: '[data-tour="presence-bar"]',
    placement: 'bottom',
    media: { kind: 'demo', id: 'presence' },
  },
  {
    id: 'group-dm',
    title: '💬 Group DMs',
    body:
      'Hit ＋ New DM to start a group chat. Pick two or more teammates and you\'ve got a private room — for planning, decisions, or lunch debates. Works exactly like a 1:1 DM, just with more people.',
    target: '[data-tour="new-dm-button"]',
    placement: 'bottom',
    media: { kind: 'demo', id: 'group-dm' },
  },
  {
    id: 'goals',
    title: '🎯 Your goals',
    body:
      'Click any goal name, description, current, or target to edit inline. Every change saves as you type and is real-time synced across everyone on the dashboard. Hover any row to reveal a × for deletion.',
    target: '[data-tour="first-goal"]',
    placement: 'top',
  },
  {
    id: 'dri-support',
    title: 'DRI + Support',
    body:
      'DRI (Directly Responsible Individual) is who\'s primarily accountable — one person, always. Support is everyone else helping — click ＋ to add teammates. Sub-goals have their own Owner column for finer accountability.',
    target: '[data-tour="goal-owners"]',
    placement: 'bottom',
    media: { kind: 'demo', id: 'dri-support' },
  },
  {
    id: 'weekly',
    title: '📋 Weekly Summary',
    body:
      'Every Monday, fill in TLDR, Wins, and Flags for each goal you own. Click any field to start typing — it saves automatically. Just 2–4 sentences per field is enough.',
    target: '[data-tour="weekly-summary"]',
    placement: 'top',
    media: { kind: 'demo', id: 'weekly-summary' },
  },
  {
    id: 'weekly-examples',
    title: '✍️ What "good" looks like',
    body:
      'TLDR should be a full paragraph of key takeaways — where the goal stands, what actually moved this week, what\'s in motion, and any context a reader needs. Wins and Flags are shorter: one or two concrete items each, with names and numbers. The better these are, the better Zoe\'s company recap reads.',
    target: '[data-tour="weekly-summary"]',
    placement: 'top',
    media: { kind: 'demo', id: 'weekly-examples' },
  },
  {
    id: 'action-items',
    title: '⚡ Action Items',
    body:
      'Add tasks, assign owners, set due dates. They automatically roll up into the Follow-ups section at the top of the dashboard — so nothing falls through the cracks.',
    target: '[data-tour="action-items"]',
    placement: 'top',
    media: { kind: 'demo', id: 'action-items' },
  },
  {
    id: 'comments',
    title: '💬 Comments + @mentions',
    body:
      'Click 💬 next to any goal or sub-goal to start a thread. Type @name to tag a teammate — they\'ll get a notification. Threads can be marked Resolved when handled.',
    target: '[data-tour="comment-bubble"]',
    placement: 'bottom',
    media: { kind: 'demo', id: 'comments' },
  },
  {
    id: 'reactions',
    title: '😀 Reactions',
    body:
      'Drop an emoji on any goal or sub-goal with the 🙂 button. Great for quick acknowledgements — 👍 on a Wins entry, 🔥 on a big update, 🎯 on a closed goal.',
    target: '[data-tour="reaction-bubble"]',
    placement: 'bottom',
    media: { kind: 'demo', id: 'reactions' },
  },
  {
    id: 'undo',
    title: '↩️ Undo',
    body:
      '⌘Z (or Ctrl+Z) anywhere on the page undoes your last change. Goals, summaries, comments, action items, owner changes — all covered. If you delete something by accident, don\'t panic.',
    target: null,
    placement: 'center',
  },
  {
    id: 'done',
    title: '🚀 You\'re set',
    body:
      'That\'s the tour. Replay it any time from the 🧭 Tour button in the top nav. Questions? DM Ricky on the dashboard.',
    target: null,
    placement: 'center',
  },
]
